import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Button, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  TimePicker, 
  message, 
  Row, 
  Col, 
  Space
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import moment, { Moment } from 'moment';
import apiService, { User, Lead, Team } from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { useApp } from '../contexts/AppContext';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';

const { Option } = Select;
const { TextArea } = Input;

interface EventModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preSelectedLead?: (Lead & { title?: string; activity_type?: string }) | null;
  isEdit?: boolean;
  editingEvent?: any;
}

const importanceLevels = [
  { value: 2, label: 'Média (1 hora)' },
  { value: 3, label: 'Alta (15 min + 1 hora)' }
];

const eventTypes = [
  { value: 'meeting', label: '🤝 Reunião' },
  { value: 'installation', label: '🔧 Instalação' },
  { value: 'follow-up', label: '📋 Follow-up' },
  { value: 'contact', label: '📞 Contacto' }
];

const EventModal: React.FC<EventModalProps> = ({ 
  open, 
  onClose, 
  onSuccess, 
  preSelectedLead,
  isEdit = false,
  editingEvent
}) => {
  const { user } = useCognitoAuth();
  const { fetchNotifications } = useApp();
  const [users, setUsers] = useState<User[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchUsers();
      fetchLeads();
      fetchTeams();
      if (isEdit && editingEvent) {
        const eventDate = moment(editingEvent.due_date);
        const endTime = moment(editingEvent.due_date).add(editingEvent.duration_minutes || 60, 'minute');
        form.setFieldsValue({
          ...editingEvent,
          event_date: eventDate,
          start_time: eventDate,
          end_time: endTime,
          assigned_to: editingEvent.persons_of_interest?.[0] || editingEvent.assigned_to,
        });
      } else {
        form.resetFields();
        
        // For Gestor Comercial users, automatically assign to themselves
        if (user?.role === 'Gestor Comercial') {
          form.setFieldsValue({
            assigned_to: user.id
          });
        }
        
        if (preSelectedLead) {
          // Check if this is an installation event (has activity_type set)
          if (preSelectedLead.activity_type === 'installation') {
            form.setFieldsValue({
              lead_id: preSelectedLead.id,
              title: preSelectedLead.title || `Instalação: ${preSelectedLead.name}`,
              activity_type: 'installation',
              importance_level: 2
            });
          } else if (preSelectedLead.activity_type === 'contact') {
            // Handle contact event
            form.setFieldsValue({
              lead_id: preSelectedLead.id,
              title: preSelectedLead.title || `Contacto: ${preSelectedLead.name}`,
              activity_type: 'contact',
              importance_level: 2
            });
        } else {
          // Default to follow-up event
          form.setFieldsValue({
            lead_id: preSelectedLead.id,
            title: `Follow-up: ${preSelectedLead.name}`,
            activity_type: 'follow-up',
            importance_level: 2
          });
        }
        
        // For Gestor Comercial users, automatically assign to themselves
        if (user?.role === 'Gestor Comercial') {
          form.setFieldsValue({
            assigned_to: user.id
          });
        }
        }
      }
    }
  }, [open, user, isEdit, editingEvent, preSelectedLead, form]);

  const fetchUsers = async () => {
    if (!user) return;
    try {
      const usersData = await apiService.getAssignableUsers();
      setUsers(usersData || []);
    } catch (error) {
      message.error('Erro ao carregar utilizadores.');
      setUsers([]);
    }
  };

  const fetchLeads = async () => {
    try {
      // For event creation, fetch all leads the user has access to
      // Don't filter by user_id to allow selecting any accessible lead
      const data = await apiService.getLeads();
      setLeads(data || []);
    } catch (error) {
      setLeads([]);
    }
  };

  const fetchTeams = async () => {
    try {
      const teamsData = await apiService.getTeams();
      setTeams(teamsData || []);
    } catch (error) {
      setTeams([]);
    }
  };

  const handleCreateEvent = async (values: any) => {
    if (!user) return;
    setLoading(true);
    try {
      // Validate required fields
      if (!values.event_date || !values.start_time) {
        message.error('Por favor selecione a data e hora do evento!');
        setLoading(false);
        return;
      }

      // Combine date and time from form values
      const eventDateTime = values.event_date
        .hour(values.start_time.hour())
        .minute(values.start_time.minute())
        .second(0);

      // Check if assigned_to is a team ID
      const selectedTeam = teams.find(team => team.id === values.assigned_to);
      
      if (selectedTeam) {
        // Create event for each team member
        const teamMembers = selectedTeam.members || [];
        if (teamMembers.length === 0) {
          message.error('A equipa selecionada não tem membros.');
          setLoading(false);
          return;
        }

        let createdCount = 0;
        for (const member of teamMembers) {
          try {
            // Always include the creator in persons_of_interest for notifications
            const personsOfInterest = [user.id]; // Creator always gets notifications
            if (member.id !== user.id) {
              personsOfInterest.push(member.id);
            }

            const eventData = {
              title: values.title,
              description: values.description,
              activity_type: values.activity_type,
              due_date: eventDateTime.format('YYYY-MM-DD HH:mm:ss'),
              importance_level: values.importance_level,
              persons_of_interest: personsOfInterest,
              status: 'pending',
              duration_minutes: values.duration_minutes || 60,
              subject: values.subject,
              assigned_to: member.id,
              created_by: user.id,
              notes: values.notes,
              tags: values.tags,
              lead_id: values.lead_id
            };

            await apiService.createActivity(eventData);
            createdCount++;
          } catch (error: any) {
            // Continue with other members even if one fails
          }
        }

        if (createdCount > 0) {
          message.success(`Evento criado com sucesso para ${createdCount} membros da equipa!`);
        } else {
          message.error('Erro ao criar eventos para a equipa.');
        }
      } else {
        // Create single event for individual user
        // Always include the creator in persons_of_interest for notifications
        const personsOfInterest = [user.id]; // Creator always gets notifications
        if (values.assigned_to && values.assigned_to !== user.id) {
          personsOfInterest.push(values.assigned_to);
        }

        const eventData = {
          title: values.title,
          description: values.description,
          activity_type: values.activity_type,
          due_date: eventDateTime.format('YYYY-MM-DD HH:mm:ss'),
          importance_level: values.importance_level,
          persons_of_interest: personsOfInterest,
          status: 'pending',
          duration_minutes: values.duration_minutes || 60,
          subject: values.subject,
          assigned_to: values.assigned_to || user.id,
          created_by: user.id,
          notes: values.notes,
          tags: values.tags,
          lead_id: values.lead_id
        };

        if (isEdit && editingEvent) {
          await apiService.updateActivity(editingEvent.id, eventData);
          message.success('Evento atualizado com sucesso!');
        } else {
          await apiService.createActivity(eventData);
          message.success('Evento criado com sucesso!');
        }
      }
      
      onClose();
      form.resetFields();
      if (onSuccess) {
        onSuccess();
      }
      fetchNotifications();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        message.error('Já existe um evento semelhante para este lead e horário.');
      } else {
        message.error('Erro ao criar evento.');
      }
    }
    setLoading(false);
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isEdit ? "Editar Evento" : "Criar Novo Evento"}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCreateEvent}
        initialValues={{ importance_level: 2 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label="Título do Evento"
              rules={[{ required: true, message: 'Por favor insira o título!' }]}
            >
              <Input placeholder="Ex: Reunião com cliente" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="activity_type"
              label="Tipo de Atividade"
              rules={[{ required: true, message: 'Por favor selecione o tipo!' }]}
            >
              <Select placeholder="Selecionar tipo">
                {eventTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="event_date"
              label="Data do Evento"
              rules={[{ required: true, message: 'Por favor selecione a data!' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="start_time"
              label="Hora de Início"
              rules={[{ required: true, message: 'Por favor selecione a hora de início!' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="end_time"
              label="Hora de Fim"
              rules={[{ required: true, message: 'Por favor selecione a hora de fim!' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="importance_level"
              label="Importância"
              rules={[{ required: true, message: 'Por favor selecione a importância!' }]}
            >
              <Select placeholder="Selecionar importância">
                {importanceLevels.map(level => (
                  <Option key={level.value} value={level.value}>
                    {level.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="assigned_to"
              label="Atribuído a"
              extra={user?.role === 'Gestor Comercial' ? "Eventos são automaticamente atribuídos a si mesmo" : "Selecionar uma equipa criará o evento para todos os membros da equipa"}
            >
              <Select
                placeholder={user?.role === 'Gestor Comercial' ? "Selecionar pessoa" : "Selecionar pessoa ou equipa"}
                showSearch
                optionFilterProp="children"
                disabled={user?.role === 'Gestor Comercial'}
              >
                {/* Individual Users */}
                {(users || []).map(user => (
                  <Option key={user.id} value={user.id}>
                    <UserOutlined /> {user.username}
                  </Option>
                ))}
                
                {/* Teams - Only show for users who can assign to teams */}
                {user?.role !== 'Gestor Comercial' && (teams || []).map(team => (
                  <Option key={`team-${team.id}`} value={team.id}>
                    <TeamOutlined /> {team.name} ({team.members?.length || 0} membros)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item
          name="description"
          label="Descrição"
        >
          <TextArea rows={3} placeholder="Descrição detalhada do evento..." />
        </Form.Item>
        
        <Form.Item
          name="notes"
          label="Notas"
        >
          <TextArea rows={2} placeholder="Notas adicionais..." />
        </Form.Item>
        
        <Form.Item
          name="lead_id"
          label="Lead (Opcional)"
          extra="Associar o evento a um lead específico"
        >
          <Select 
            showSearch 
            optionFilterProp="children" 
            placeholder="Selecionar lead (opcional)"
            disabled={preSelectedLead != null}
            allowClear
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {(leads || []).map(lead => (
              <Option key={lead.id} value={lead.id}>
                {lead.dif} - <span style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</span> {lead.company ? `(${lead.company})` : ''} {lead.phone && `(${lead.phone})`}
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              icon={isEdit ? <EditOutlined /> : <PlusOutlined />}
            >
              {isEdit ? 'Guardar Alterações' : 'Criar Evento'}
            </Button>
            <Button onClick={handleCancel}>
              Cancelar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EventModal; 